import React, { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import {
  AppstoreOutlined,
  BarChartOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  InboxOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import cccApi, {
  type CccContractSubmissionRow,
  type CccProposalStatRow,
  type CccSignatureStatRow,
} from '../../../api/cccApi';
import type { CccDashboardFilters } from '../types';
import CccContractSubmissionsTable from '../CccContractSubmissionsTable';
import WonderTradeLoader from '@crm/components/Shared/WonderTradeLoader';
import '@crm/components/BackOffice/BackOfficeEstatisticas.css';

const PAGE_SIZE = 20;

const stripAccentsLower = (s: string) =>
  s
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

type StatusCounterMod =
  | 'concluido'
  | 'cancelado'
  | 'progresso'
  | 'disponivel'
  | 'reclamada'
  | 'default';

const isExactEmProgresso = (n: string) =>
  n === 'em progresso' || n === 'in progress' || n === 'in_progress' || n === 'em_progresso';

const statusLabelToCounterMod = (label: string): StatusCounterMod => {
  const n = stripAccentsLower(label);
  if (!n || n === '—') return 'default';
  if (n.includes('isolado') && n.includes('concluid')) return 'concluido';
  if (n.includes('isolado') && n.includes('cancel')) return 'cancelado';
  if (n.startsWith('concluid')) return 'concluido';
  if (n.startsWith('cancel')) return 'cancelado';
  if (isExactEmProgresso(n)) return 'progresso';
  if (n === 'disponivel' || n === 'available') return 'disponivel';
  if (n === 'reclamada' || n === 'claimed') return 'reclamada';
  return 'default';
};

const statusCounterBucket = (status: string): string => {
  const st = status.trim() || '—';
  const n = stripAccentsLower(st);
  if (n.includes('isolado') && n.includes('concluid')) return 'Concluído (isolado)';
  if (n.includes('isolado') && n.includes('cancel')) return 'Cancelado (isolado)';
  if (n.startsWith('concluid')) return 'Concluído';
  if (n.startsWith('cancel')) return 'Cancelado';
  return st;
};

const matchesStatusFilter = (status: string, filter: string): boolean => {
  const st = status.trim() || '—';
  const n = stripAccentsLower(st);
  if (filter === 'Concluído') return n.startsWith('concluid') && !n.includes('isolado');
  if (filter === 'Cancelado') return n.startsWith('cancel') && !n.includes('isolado');
  if (filter === 'Concluído (isolado)') return n.includes('isolado') && n.startsWith('concluid');
  if (filter === 'Cancelado (isolado)') return n.includes('isolado') && n.startsWith('cancel');
  return st === filter;
};

const statusCounterSortKey = (label: string): number => {
  const n = stripAccentsLower(label);
  if (n.includes('isolado') && n.includes('concluid')) return 52;
  if (n.includes('isolado') && n.includes('cancel')) return 62;
  if (n.startsWith('concluid')) return 50;
  if (n.startsWith('cancel')) return 60;
  if (isExactEmProgresso(n)) return 30;
  if (n === 'disponivel' || n === 'available') return 10;
  if (n === 'reclamada' || n === 'claimed') return 20;
  if (n === 'op criada') return 22;
  if (n.startsWith('proposta em aprov')) return 24;
  if (n.includes('exclusividade') || n.includes('pendente gestor')) return 26;
  return 40;
};

const StatusCounterIcon: React.FC<{ mod: StatusCounterMod }> = ({ mod }) => {
  const cls = 'bo-estatisticas-counter-icon-svg';
  switch (mod) {
    case 'concluido':
      return <CheckSquareOutlined className={cls} />;
    case 'cancelado':
      return <CloseCircleOutlined className={cls} />;
    case 'progresso':
      return <ClockCircleOutlined className={cls} />;
    case 'disponivel':
      return <InboxOutlined className={cls} />;
    case 'reclamada':
      return <UserOutlined className={cls} />;
    default:
      return <AppstoreOutlined className={cls} />;
  }
};

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const userNameOnly = (username?: string | null) => {
  const u = (username || '').trim();
  if (!u || u === 'N/A') return '—';
  const mark = u.match(/\s\((Eliminado|Suspenso)\)$/);
  const base = mark ? u.slice(0, mark.index).trim() : u;
  const suffix = mark ? mark[0] : '';
  if (base.includes('@')) {
    const local = base.split('@')[0]?.trim();
    return `${local || '—'}${suffix}`;
  }
  return `${base}${suffix}`;
};

const TABLE_TABS: Array<{ key: TableMode; label: string; title: string }> = [
  { key: '1', label: 'Pedidos', title: 'Pedidos de proposta' },
  { key: '2', label: 'Serviços', title: 'Serviços' },
  { key: '3', label: 'Contratos', title: 'Contratos' },
  { key: '4', label: 'Assinaturas', title: 'Assinaturas' },
];

const targetUserLabel = (user: { name: string; status: 'active' | 'suspended' | 'deleted' }) => {
  if (user.status === 'deleted') return `${user.name} (Eliminado)`;
  if (user.status === 'suspended') return `${user.name} (Suspenso)`;
  return user.name;
};

const nifDigits = (value: string | null | undefined) => String(value || '').replace(/\D/g, '');

const matchesNifQuery = (nif: string | null | undefined, query: string) => {
  const q = nifDigits(query);
  if (!q) return true;
  return nifDigits(nif).includes(q);
};

type Props = {
  filters: CccDashboardFilters;
  fromDate: string;
  toDate: string;
};

type TableMode = '1' | '2' | '3' | '4';

type SignatureStatusMod = 'pending' | 'enviado' | 'assinado' | 'cancelado' | 'default';

const signatureStatusMod = (statusRaw: string): SignatureStatusMod => {
  const s = (statusRaw || '').toLowerCase();
  if (s === 'pending') return 'pending';
  if (s === 'enviado') return 'enviado';
  if (s === 'assinado' || s === 'completed') return 'assinado';
  if (s === 'cancelled') return 'cancelado';
  return 'default';
};

const SignatureStatusCounterIcon: React.FC<{ mod: SignatureStatusMod }> = ({ mod }) => {
  const cls = 'bo-estatisticas-counter-icon-svg';
  switch (mod) {
    case 'assinado':
      return <CheckSquareOutlined className={cls} />;
    case 'cancelado':
      return <CloseCircleOutlined className={cls} />;
    case 'enviado':
      return <SendOutlined className={cls} />;
    case 'pending':
      return <ClockCircleOutlined className={cls} />;
    default:
      return <EditOutlined className={cls} />;
  }
};

const SIGNATURE_STATUS_COUNTER_MOD: Record<string, SignatureStatusMod> = {
  pending: 'pending',
  enviado: 'enviado',
  assinado: 'assinado',
  cancelled: 'cancelado',
};

const CONTRACT_STATUS_COUNTER_MOD: Record<string, StatusCounterMod> = {
  available: 'disponivel',
  in_progress: 'progresso',
  enviado: 'progresso',
  pending: 'progresso',
  objected: 'cancelado',
  installed: 'concluido',
  cancelled: 'cancelado',
  aguarda_agendamento_vt: 'progresso',
  aguardo_confirmacao: 'progresso',
  reformulacao: 'cancelado',
  reformulado_ok: 'progresso',
  vt_realizada_ok: 'progresso',
  aceite: 'concluido',
};

const CccProposalEstatisticasSection: React.FC<Props> = ({ filters, fromDate, toDate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<CccProposalStatRow[]>([]);
  const [servicoRows, setServicoRows] = useState<CccProposalStatRow[]>([]);
  const [contractRows, setContractRows] = useState<CccContractSubmissionRow[]>([]);
  const [signatureRows, setSignatureRows] = useState<CccSignatureStatRow[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [signaturesLoading, setSignaturesLoading] = useState(false);
  const [contractsError, setContractsError] = useState<string | null>(null);
  const [signaturesError, setSignaturesError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string | null>(null);
  const [activeServicoFilters, setActiveServicoFilters] = useState<string[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableMode, setTableMode] = useState<TableMode>('1');
  const [targetUserIds, setTargetUserIds] = useState<number[]>([]);
  const [targetUsers, setTargetUsers] = useState<
    Array<{ id: number; name: string; status: 'active' | 'suspended' | 'deleted' }>
  >([]);
  const [targetUsersLoading, setTargetUsersLoading] = useState(false);
  const [nifQuery, setNifQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!fromDate || !toDate) return;

    setLoading(true);
    setError(null);
    const params: Record<string, string> = {
      from_date: fromDate,
      to_date: toDate,
    };
    if (filters.selectedWorkspaces.length) {
      params.workspaces = filters.selectedWorkspaces.join(',');
    }
    if (targetUserIds.length) {
      params.users = targetUserIds.join(',');
    }

    cccApi
      .getCccDashboardProposalRequests(params)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setError(res.message || 'Não foi possível carregar pedidos.');
          setRows([]);
          setServicoRows([]);
          return;
        }
        setRows(Array.isArray(res.rows) ? res.rows : []);
        setServicoRows(Array.isArray(res.servico_rows) ? res.servico_rows : []);
        setPage(1);
        setActiveStatusFilter(null);
        setActiveServicoFilters([]);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Erro ao carregar pedidos de proposta';
        setError(String(msg));
        setRows([]);
        setServicoRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, filters.selectedWorkspaces, targetUserIds, refreshTick]);

  useEffect(() => {
    let cancelled = false;
    if (tableMode !== '3' || !fromDate || !toDate) return;

    setContractsLoading(true);
    setContractsError(null);
    const params: Record<string, string> = {
      from_date: fromDate,
      to_date: toDate,
    };
    if (filters.selectedWorkspaces.length) {
      params.workspaces = filters.selectedWorkspaces.join(',');
    }
    if (targetUserIds.length) {
      params.users = targetUserIds.join(',');
    }

    cccApi
      .getCccDashboardContractSubmissions(params)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setContractsError(res.message || 'Não foi possível carregar submissões de contrato.');
          setContractRows([]);
          return;
        }
        setContractRows(Array.isArray(res.rows) ? res.rows : []);
        setPage(1);
        setActiveStatusFilter(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Erro ao carregar submissões de contrato';
        setContractsError(String(msg));
        setContractRows([]);
      })
      .finally(() => {
        if (!cancelled) setContractsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    tableMode,
    fromDate,
    toDate,
    filters.selectedWorkspaces,
    targetUserIds,
    refreshTick,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (tableMode !== '4' || !fromDate || !toDate) return;

    setSignaturesLoading(true);
    setSignaturesError(null);
    const params: Record<string, string> = {
      from_date: fromDate,
      to_date: toDate,
    };
    if (filters.selectedWorkspaces.length) {
      params.workspaces = filters.selectedWorkspaces.join(',');
    }
    if (targetUserIds.length) {
      params.users = targetUserIds.join(',');
    }

    cccApi
      .getCccDashboardSignatureRequests(params)
      .then((res) => {
        if (cancelled) return;
        if (!res.success) {
          setSignaturesError(res.message || 'Não foi possível carregar pedidos de assinatura.');
          setSignatureRows([]);
          return;
        }
        setSignatureRows(Array.isArray(res.rows) ? res.rows : []);
        setPage(1);
        setActiveStatusFilter(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Erro ao carregar pedidos de assinatura';
        setSignaturesError(String(msg));
        setSignatureRows([]);
      })
      .finally(() => {
        if (!cancelled) setSignaturesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    tableMode,
    fromDate,
    toDate,
    filters.selectedWorkspaces,
    targetUserIds,
    refreshTick,
  ]);

  useEffect(() => {
    let cancelled = false;
    setTargetUsersLoading(true);
    cccApi
      .getCccDashboardProposalTargetUsers()
      .then((res) => {
        if (cancelled) return;
        setTargetUsers(Array.isArray(res.users) ? res.users : []);
      })
      .catch(() => {
        if (!cancelled) setTargetUsers([]);
      })
      .finally(() => {
        if (!cancelled) setTargetUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rowsMatchingNif = useMemo(
    () => rows.filter((row) => matchesNifQuery(row.nif, nifQuery)),
    [rows, nifQuery],
  );
  const servicoRowsMatchingNif = useMemo(
    () => servicoRows.filter((row) => matchesNifQuery(row.nif, nifQuery)),
    [servicoRows, nifQuery],
  );
  const contractRowsMatchingNif = useMemo(
    () => contractRows.filter((row) => matchesNifQuery(row.nif, nifQuery)),
    [contractRows, nifQuery],
  );
  const signatureRowsMatchingNif = useMemo(
    () => signatureRows.filter((row) => matchesNifQuery(row.nif, nifQuery)),
    [signatureRows, nifQuery],
  );

  const activeRows =
    tableMode === '1'
      ? rowsMatchingNif
      : tableMode === '2'
        ? servicoRowsMatchingNif
        : tableMode === '3'
          ? contractRowsMatchingNif
          : signatureRowsMatchingNif;

  useEffect(() => {
    setPage(1);
    setActiveStatusFilter(null);
    setActiveServicoFilters([]);
  }, [tableMode]);

  const statusCounterEntries = useMemo(() => {
    if (tableMode === '4') {
      const defs: Array<{ key: string; label: string; mod: SignatureStatusMod }> = [
        { key: 'pending', label: 'Por resolver', mod: 'pending' },
        { key: 'enviado', label: 'Enviado', mod: 'enviado' },
        { key: 'assinado', label: 'Assinado', mod: 'assinado' },
        { key: 'cancelled', label: 'Cancelado', mod: 'cancelado' },
      ];
      const counts = new Map<string, number>();
      for (const row of signatureRowsMatchingNif) {
        const raw = (row.status_raw || '').toLowerCase();
        const bucket = raw === 'completed' ? 'assinado' : raw;
        counts.set(bucket, (counts.get(bucket) || 0) + 1);
      }
      return defs
        .map((def) => ({
          label: def.label,
          count:
            def.key === 'assinado'
              ? counts.get('assinado') || 0
              : counts.get(def.key) || 0,
          mod: def.mod,
          status: def.key,
        }))
        .filter((e) => e.count > 0);
    }
    if (tableMode === '3') {
      const map = new Map<string, { label: string; count: number; mod: StatusCounterMod }>();
      for (const row of contractRowsMatchingNif) {
        const label = row.status_label || row.status || 'available';
        const prev = map.get(label);
        map.set(label, {
          label,
          count: (prev?.count || 0) + 1,
          mod: CONTRACT_STATUS_COUNTER_MOD[row.status] || 'default',
        });
      }
      return Array.from(map.entries())
        .filter(([, { count }]) => count > 0)
        .map(([status, entry]) => ({ label: entry.label, count: entry.count, mod: entry.mod, status }))
        .sort((a, b) => a.label.localeCompare(b.label, 'pt'));
    }
    const map = new Map<string, number>();
    for (const row of activeRows) {
      const bucket = statusCounterBucket(row.status || '—');
      map.set(bucket, (map.get(bucket) || 0) + 1);
    }
    return Array.from(map.entries())
      .filter(([, count]) => count > 0)
      .map(([label, count]) => ({
        label,
        count,
        mod: statusLabelToCounterMod(label),
      }))
      .sort((a, b) => {
        const dk = statusCounterSortKey(a.label) - statusCounterSortKey(b.label);
        return dk !== 0 ? dk : a.label.localeCompare(b.label, 'pt');
      });
  }, [activeRows, contractRowsMatchingNif, signatureRowsMatchingNif, tableMode]);

  const servicoCounterEntries = useMemo(() => {
    if (tableMode === '3' || tableMode === '4') return [];
    const counts = new Map<string, { label: string; count: number }>();
    for (const row of activeRows) {
      const key = row.servico_kind || 'other';
      const label = row.servico || key;
      const prev = counts.get(key);
      counts.set(key, { label, count: (prev?.count || 0) + 1 });
    }
    return Array.from(counts.entries())
      .map(([key, { label, count }]) => ({ key, label, count }))
      .filter((e) => e.count > 0);
  }, [activeRows, tableMode]);

  const filteredRows = useMemo(() => {
    if (tableMode === '4') {
      let list = signatureRowsMatchingNif;
      if (activeStatusFilter) {
        list = list.filter((row) => {
          const entry = statusCounterEntries.find(
            (e) => 'status' in e && e.label === activeStatusFilter
          );
          if (entry && 'status' in entry) {
            const raw = (row.status_raw || '').toLowerCase();
            const bucket = raw === 'completed' ? 'assinado' : raw;
            return bucket === entry.status;
          }
          return row.status === activeStatusFilter;
        });
      }
      return list;
    }
    if (tableMode === '3') {
      let list = contractRowsMatchingNif;
      if (activeStatusFilter) {
        list = list.filter((row) => (row.status_label || row.status) === activeStatusFilter);
      }
      return list;
    }
    let list = activeRows as CccProposalStatRow[];
    if (activeStatusFilter) {
      list = list.filter((row) => matchesStatusFilter(row.status || '—', activeStatusFilter));
    }
    if (activeServicoFilters.length) {
      const allowed = new Set(activeServicoFilters);
      list = list.filter((row) => allowed.has(row.servico_kind || 'other'));
    }
    return list;
  }, [activeRows, activeStatusFilter, activeServicoFilters, contractRowsMatchingNif, signatureRowsMatchingNif, statusCounterEntries, tableMode]);

  const sectionLoading =
    tableMode === '3' ? contractsLoading : tableMode === '4' ? signaturesLoading : loading;
  const sectionError =
    tableMode === '3' ? contractsError : tableMode === '4' ? signaturesError : error;
  const totalCount = activeRows.length;

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const sectionTitle = useMemo(() => {
    const base = TABLE_TABS.find((tab) => tab.key === tableMode)?.title || 'Pedidos de proposta';
    if (!targetUserIds.length) return base;
    const names = targetUserIds
      .map((id) => targetUsers.find((user) => user.id === id))
      .filter((user): user is (typeof targetUsers)[number] => Boolean(user))
      .map((user) => targetUserLabel(user));
    if (!names.length) return base;
    const shown = names.slice(0, 2).join(', ');
    const extra = names.length > 2 ? ` e mais ${names.length - 2}` : '';
    return `${base} — ${shown}${extra}`;
  }, [tableMode, targetUserIds, targetUsers]);

  const toggleServico = (key: string) => {
    setActiveServicoFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
    setPage(1);
  };

  return (
    <section className="ccc-monitor-section ccc-proposal-estatisticas">
      <header className="ccc-monitor-section-head">
        <div>
          <h2>{sectionTitle}</h2>
        </div>
        <div className="ccc-monitor-section-actions">
          <button
            type="button"
            className="ccc-monitor-refresh-btn"
            disabled={sectionLoading}
            onClick={() => setRefreshTick((n) => n + 1)}
          >
            {sectionLoading ? 'A atualizar…' : 'Atualizar'}
          </button>
          <span className="ccc-monitor-count">{totalCount}</span>
        </div>
      </header>

      <div className="ccc-proposal-toolbar">
        <div className="ccc-proposal-tabs" role="tablist" aria-label="Vista da tabela">
          {TABLE_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={tableMode === tab.key}
              className={`ccc-proposal-tab${tableMode === tab.key ? ' active' : ''}`}
              onClick={() => setTableMode(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ccc-proposal-user-row">
        <label htmlFor="ccc-proposal-target-users">Utilizador</label>
        <Select<number[]>
          id="ccc-proposal-target-users"
          className="ccc-proposal-user-filter"
          mode="multiple"
          value={targetUserIds}
          onChange={(ids) => {
            setTargetUserIds(ids);
            setPage(1);
          }}
          placeholder="Todos os utilizadores"
          loading={targetUsersLoading}
          allowClear
          showSearch
          optionFilterProp="label"
          maxTagCount="responsive"
          style={{ width: 360 }}
          options={targetUsers.map((user) => ({
            value: user.id,
            label: targetUserLabel(user),
          }))}
        />
        <label htmlFor="ccc-proposal-nif-search">NIF</label>
        <input
          id="ccc-proposal-nif-search"
          className="ccc-proposal-nif-search"
          type="search"
          value={nifQuery}
          placeholder="Pesquisar por NIF"
          onChange={(e) => {
            setNifQuery(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {sectionLoading ? (
        <div className="bo-estatisticas-loading">
          <WonderTradeLoader fullScreen={false} whiteBackground={false} />
        </div>
      ) : sectionError ? (
        <div className="ccc-dashboard-error">{sectionError}</div>
      ) : (
        <>
          <div className="bo-estatisticas-counters-stack">
            {tableMode === '1' ? (
              <div className="bo-estatisticas-counters" role="region" aria-label="Distribuição por estado">
                <button
                  type="button"
                  className={`bo-estatisticas-counter bo-estatisticas-counter--total${
                    activeStatusFilter === null ? ' bo-estatisticas-servico-counter--active' : ''
                  }`}
                  onClick={() => {
                    setActiveStatusFilter(null);
                    setPage(1);
                  }}
                >
                  <div className="bo-estatisticas-counter-main">
                    <span className="bo-estatisticas-counter-label">Total pedidos</span>
                    <span className="bo-estatisticas-counter-value">{rowsMatchingNif.length}</span>
                  </div>
                  <div className="bo-estatisticas-counter-icon" aria-hidden>
                    <BarChartOutlined className="bo-estatisticas-counter-icon-svg" />
                  </div>
                </button>
                {statusCounterEntries.map(({ label, count, mod }) => (
                  <button
                    type="button"
                    key={label}
                    className={`bo-estatisticas-counter bo-estatisticas-counter--${mod}${
                      activeStatusFilter === label ? ' bo-estatisticas-servico-counter--active' : ''
                    }`}
                    onClick={() => {
                      setActiveStatusFilter((prev) => (prev === label ? null : label));
                      setPage(1);
                    }}
                  >
                    <div className="bo-estatisticas-counter-main">
                      <span className="bo-estatisticas-counter-label">{label}</span>
                      <span className="bo-estatisticas-counter-value">{count}</span>
                    </div>
                    <div className="bo-estatisticas-counter-icon" aria-hidden>
                      <StatusCounterIcon mod={mod} />
                    </div>
                  </button>
                ))}
              </div>
            ) : tableMode === '2' ? (
              <>
                <div className="bo-estatisticas-counters" role="region" aria-label="Distribuição por estado">
                  <button
                    type="button"
                    className={`bo-estatisticas-counter bo-estatisticas-counter--total${
                      activeStatusFilter === null && activeServicoFilters.length === 0
                        ? ' bo-estatisticas-servico-counter--active'
                        : ''
                    }`}
                    onClick={() => {
                      setActiveStatusFilter(null);
                      setActiveServicoFilters([]);
                      setPage(1);
                    }}
                  >
                    <div className="bo-estatisticas-counter-main">
                      <span className="bo-estatisticas-counter-label">Total serviços</span>
                      <span className="bo-estatisticas-counter-value">{servicoRowsMatchingNif.length}</span>
                    </div>
                    <div className="bo-estatisticas-counter-icon" aria-hidden>
                      <BarChartOutlined className="bo-estatisticas-counter-icon-svg" />
                    </div>
                  </button>
                  {statusCounterEntries.map(({ label, count, mod }) => (
                    <button
                      type="button"
                      key={label}
                      className={`bo-estatisticas-counter bo-estatisticas-counter--${mod}${
                        activeStatusFilter === label ? ' bo-estatisticas-servico-counter--active' : ''
                      }`}
                      onClick={() => {
                        setActiveStatusFilter((prev) => (prev === label ? null : label));
                        setPage(1);
                      }}
                    >
                      <div className="bo-estatisticas-counter-main">
                        <span className="bo-estatisticas-counter-label">{label}</span>
                        <span className="bo-estatisticas-counter-value">{count}</span>
                      </div>
                      <div className="bo-estatisticas-counter-icon" aria-hidden>
                        <StatusCounterIcon mod={mod} />
                      </div>
                    </button>
                  ))}
                </div>
                {servicoCounterEntries.length > 0 && (
                  <div
                    className="bo-estatisticas-counters bo-estatisticas-counters--servico"
                    role="group"
                    aria-label="Filtrar por serviço"
                  >
                    {servicoCounterEntries.map(({ key, label, count }) => {
                      const active = activeServicoFilters.includes(key);
                      const modClass = key === 'other' ? 'outro' : key;
                      return (
                        <button
                          key={key}
                          type="button"
                          className={`bo-estatisticas-servico-counter bo-estatisticas-servico-counter--${modClass}${
                            active ? ' bo-estatisticas-servico-counter--active' : ''
                          }`}
                          aria-pressed={active}
                          onClick={() => toggleServico(key)}
                        >
                          <span className="bo-estatisticas-servico-counter-label">{label}</span>
                          <span className="bo-estatisticas-servico-counter-value">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : tableMode === '3' ? (
              <div className="bo-estatisticas-counters" role="region" aria-label="Distribuição por estado do contrato">
                <button
                  type="button"
                  className={`bo-estatisticas-counter bo-estatisticas-counter--total${
                    activeStatusFilter === null ? ' bo-estatisticas-servico-counter--active' : ''
                  }`}
                  onClick={() => {
                    setActiveStatusFilter(null);
                    setPage(1);
                  }}
                >
                  <div className="bo-estatisticas-counter-main">
                    <span className="bo-estatisticas-counter-label">Total submissões</span>
                    <span className="bo-estatisticas-counter-value">{contractRowsMatchingNif.length}</span>
                  </div>
                  <div className="bo-estatisticas-counter-icon" aria-hidden>
                    <BarChartOutlined className="bo-estatisticas-counter-icon-svg" />
                  </div>
                </button>
                {statusCounterEntries.map(({ label, count, mod }) => (
                  <button
                    type="button"
                    key={label}
                    className={`bo-estatisticas-counter bo-estatisticas-counter--${mod}${
                      activeStatusFilter === label ? ' bo-estatisticas-servico-counter--active' : ''
                    }`}
                    onClick={() => {
                      setActiveStatusFilter((prev) => (prev === label ? null : label));
                      setPage(1);
                    }}
                  >
                    <div className="bo-estatisticas-counter-main">
                      <span className="bo-estatisticas-counter-label">{label}</span>
                      <span className="bo-estatisticas-counter-value">{count}</span>
                    </div>
                    <div className="bo-estatisticas-counter-icon" aria-hidden>
                      <StatusCounterIcon mod={mod as StatusCounterMod} />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bo-estatisticas-counters" role="region" aria-label="Distribuição por estado da assinatura">
                <button
                  type="button"
                  className={`bo-estatisticas-counter bo-estatisticas-counter--total${
                    activeStatusFilter === null ? ' bo-estatisticas-servico-counter--active' : ''
                  }`}
                  onClick={() => {
                    setActiveStatusFilter(null);
                    setPage(1);
                  }}
                >
                  <div className="bo-estatisticas-counter-main">
                    <span className="bo-estatisticas-counter-label">Total pedidos</span>
                    <span className="bo-estatisticas-counter-value">{signatureRowsMatchingNif.length}</span>
                  </div>
                  <div className="bo-estatisticas-counter-icon" aria-hidden>
                    <BarChartOutlined className="bo-estatisticas-counter-icon-svg" />
                  </div>
                </button>
                {statusCounterEntries.map(({ label, count, mod }) => (
                  <button
                    type="button"
                    key={label}
                    className={`bo-estatisticas-counter bo-estatisticas-counter--sig-${mod}${
                      activeStatusFilter === label ? ' bo-estatisticas-servico-counter--active' : ''
                    }`}
                    onClick={() => {
                      setActiveStatusFilter((prev) => (prev === label ? null : label));
                      setPage(1);
                    }}
                  >
                    <div className="bo-estatisticas-counter-main">
                      <span className="bo-estatisticas-counter-label">{label}</span>
                      <span className="bo-estatisticas-counter-value">{count}</span>
                    </div>
                    <div className="bo-estatisticas-counter-icon" aria-hidden>
                      <SignatureStatusCounterIcon mod={mod as SignatureStatusMod} />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bo-estatisticas-table-wrap">
            {filteredRows.length === 0 ? (
              <div className="ccc-dashboard-empty">
                {nifDigits(nifQuery)
                  ? 'Nenhum resultado para este NIF.'
                  : tableMode === '4'
                    ? 'Sem pedidos de assinatura digital no período selecionado.'
                    : tableMode === '3'
                      ? 'Sem submissões de contrato no período selecionado.'
                      : 'Sem pedidos de proposta no período selecionado.'}
              </div>
            ) : (
              <>
                {tableMode === '1' ? (
                  <table className="bo-estatisticas-table">
                    <thead>
                      <tr>
                        <th>Empresa</th>
                        <th>NIF</th>
                        <th>Área CCC</th>
                        <th>Serviço</th>
                        <th>Data do pedido</th>
                        <th>Submetido por (CCC)</th>
                        <th>BackOffice</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(paginatedRows as CccProposalStatRow[]).map((row) => (
                        <tr key={row.id}>
                          <td>{row.nome_empresa}</td>
                          <td>{row.nif || '—'}</td>
                          <td>{row.workspace_label}</td>
                          <td>
                            <span
                              className={`bo-estatisticas-servico bo-estatisticas-servico--${
                                row.servico_kind === 'other' ? 'outro' : row.servico_kind || 'none'
                              }`}
                            >
                              {row.servico}
                            </span>
                          </td>
                          <td>{fmtDateTime(row.data_pedido)}</td>
                          <td>{row.submitted_by_name}</td>
                          <td>{row.backoffice_claimed_by || '—'}</td>
                          <td>{row.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : tableMode === '2' ? (
                  <table className="bo-estatisticas-table">
                    <thead>
                      <tr>
                        <th>Nome Empresa</th>
                        <th>NIF Empresa</th>
                        <th>Serviço</th>
                        <th>CPE / CUI</th>
                        <th>Data do Pedido</th>
                        <th>Iniciado por (BackOffice)</th>
                        <th>Partilhado com</th>
                        <th>Comercial</th>
                        <th>Estado</th>
                        <th>Concluído / cancelado em</th>
                        <th>Atribuído em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(paginatedRows as CccProposalStatRow[]).map((row) => (
                        <tr key={row.id}>
                          <td>{row.nome_empresa}</td>
                          <td>{row.nif || '—'}</td>
                          <td>
                            <span
                              className={`bo-estatisticas-servico bo-estatisticas-servico--${
                                row.servico_kind === 'other' ? 'outro' : row.servico_kind || 'none'
                              }`}
                            >
                              {row.servico || '—'}
                            </span>
                          </td>
                          <td className="bo-estatisticas-cpe-cui">{row.cpe_cui || '—'}</td>
                          <td>{fmtDateTime(row.data_pedido)}</td>
                          <td>{row.backoffice_claimed_by || '—'}</td>
                          <td>—</td>
                          <td>{userNameOnly(row.submitted_by_name)}</td>
                          <td>
                            <span
                              className={`bo-estatisticas-status${
                                row.status_isolated ? ' bo-estatisticas-status--isolated' : ''
                              }`}
                            >
                              {row.status || '—'}
                            </span>
                          </td>
                          <td>{fmtDateTime(row.data_conclusao_cancelamento)}</td>
                          <td>{fmtDateTime(row.data_atribuicao)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : tableMode === '3' ? (
                  <CccContractSubmissionsTable
                    rows={filteredRows as CccContractSubmissionRow[]}
                    page={currentPage}
                    pageSize={PAGE_SIZE}
                    onPageChange={setPage}
                  />
                ) : (
                  <table className="bo-estatisticas-table bo-estatisticas-table--assinaturas">
                    <thead>
                      <tr>
                        <th>Empresa</th>
                        <th>NIF</th>
                        <th>Área CCC</th>
                        <th>IBAN</th>
                        <th>BIC / SWIFT</th>
                        <th>Representante</th>
                        <th>Email Rep.</th>
                        <th>Contacto Rep.</th>
                        <th>Submetido por (CCC)</th>
                        <th>Estado</th>
                        <th>Data do Pedido</th>
                        <th>Assinado / cancelado em</th>
                        <th>Data prevista</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(paginatedRows as CccSignatureStatRow[]).map((row) => (
                        <tr key={row.id}>
                          <td>{row.company_name || '—'}</td>
                          <td>{row.nif || '—'}</td>
                          <td>{row.workspace_label || '—'}</td>
                          <td className="bo-estatisticas-mono">{row.iban || '—'}</td>
                          <td className="bo-estatisticas-mono">{row.bic || row.swift || '—'}</td>
                          <td>{row.representative_name || '—'}</td>
                          <td>{row.representative_email || '—'}</td>
                          <td>{row.representative_contact || '—'}</td>
                          <td>{userNameOnly(row.submitted_by_name)}</td>
                          <td>
                            <span
                              className={`bo-estatisticas-status bo-estatisticas-status--sig-${signatureStatusMod(
                                row.status_raw
                              )}`}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td>{fmtDateTime(row.created_at)}</td>
                          <td>{fmtDateTime(row.completed_cancelled_at)}</td>
                          <td>{fmtDateTime(row.expected_signature_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {tableMode !== '3' && totalPages > 1 && (
                  <div className="ccc-estatisticas-pagination">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </button>
                    <span>
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Seguinte
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default CccProposalEstatisticasSection;
